import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { StatusService } from '../../../../core/services/status.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Status } from '../../../../core/models/status';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RegexConstants } from '../../../../core/constants/regex-constants';
import { RequiredMarkDirective } from '../../../../shared/directives/required-mark.directive';

@Component({
  selector: 'em-status-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputTextModule, ButtonModule, ToastModule, TranslatePipe, RequiredMarkDirective],
  templateUrl: './status-form.component.html',
  styleUrl: './status-form.component.scss',
  providers: [MessageService],
})
export class StatusFormComponent {
  form!: FormGroup;
  formErrors: any;
  isEditMode: boolean = false;
  errorMessages: any = {};
  translations: any = {};

  constructor(
    private fb: FormBuilder,
    private statusService: StatusService,
    private ref: DynamicDialogRef,
    private messageService: MessageService,
    private config: DynamicDialogConfig,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.loadTranslations();
    this.loadErrorMessages();

    this.form = this.fb.group({
      id: [null],
      status: [
        null,
        Validators.compose([
          Validators.required,
          Validators.pattern(RegexConstants.STATUS_NAME),
        ]),
      ],
      statusEs: [
        null,
        Validators.compose([
          Validators.required,
          Validators.pattern(RegexConstants.STATUS_NAME_ES),
        ]),
      ],
      statusAr: [
        null,
        Validators.compose([
          Validators.required,
          Validators.pattern(RegexConstants.STATUS_NAME_AR),
        ]),
      ],
    });

    if (this.config.data) {
      this.isEditMode = true;
      this.populateForm(this.config.data);
    }
    this.translate.onLangChange.subscribe(() => {
      this.loadErrorMessages();
    });
  }

  loadErrorMessages(): void {
    this.translate
      .get([
        'layout.configurations.status.statusValidators.required',
        'layout.configurations.status.statusValidators.requiredEs',
        'layout.configurations.status.statusValidators.requiredAr',
        'layout.configurations.status.statusValidators.pattern',
        'layout.configurations.status.statusValidators.patternEs',
        'layout.configurations.status.statusValidators.patternAr',
      ])
      .subscribe((translations) => {
        this.errorMessages = {
          required: {
            status: translations['layout.configurations.status.statusValidators.required'],
            statusEs: translations['layout.configurations.status.statusValidators.requiredEs'],
            statusAr: translations['layout.configurations.status.statusValidators.requiredAr'],
          },
          pattern: {
            status: translations['layout.configurations.status.statusValidators.pattern'],
            statusEs: translations['layout.configurations.status.statusValidators.patternEs'],
            statusAr: translations['layout.configurations.status.statusValidators.patternAr'],
          },
        };
      });
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.status.addStatus.errorMessage.alreadyExists',
        'layout.configurations.status.editStatus.errorMessage.alreadyExists',
        'layout.configurations.status.addStatus.successMessage.title',
        'layout.configurations.status.editStatus.successMessage.title',
        'layout.configurations.status.editStatus.errorMessage.title',
        'layout.configurations.status.editStatus.errorMessage.detail',
        'layout.configurations.status.addStatus.errorMessage.title',
        'layout.configurations.status.addStatus.errorMessage.detail',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  populateForm(status: Status) {
    this.form.patchValue({
      id: status.id,
      status: status.status,
      statusEs: status.statusEs,
      statusAr: status.statusAr,
    });
  }

  onSave() {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((control) => {
        control.markAsTouched();
      });
      return;
    }

    if (this.isEditMode) {
      this.updateStatus();
    } else {
      this.addStatus();
    }
  }

  addStatus() {
    this.statusService.addStatus(this.form.value).subscribe({
      next: (status: Status) => {
        this.ref.close(status);
      },
      error: (error) => {
        this.formErrors = error?.error?.fieldErrors;
        console.log('Error while saving the status: ', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.status.addStatus.errorMessage.title'],
          detail: this.translations['layout.configurations.status.addStatus.errorMessage.alreadyExists'],
        });
      },
    });
  }

  updateStatus() {
    const originalStatus: Status = this.config.data;
    const updatedStatus: Status = this.form.value;
    const isChanged =
      originalStatus.status !== updatedStatus.status ||
      originalStatus.statusEs !== updatedStatus.statusEs ||
      originalStatus.statusAr !== updatedStatus.statusAr;
    if (!isChanged) {
      this.ref.close({ type: 'no-change' });
      return;
    }

    this.statusService.updateStatus(this.form.value).subscribe({
      next: (status: Status) => {
        this.ref.close({ type: 'updated', data: status });
      },
      error: (error) => {
        this.formErrors = error?.error?.fieldErrors;
        console.log('Error while updating the status: ', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.status.editStatus.errorMessage.title'],
          detail: this.translations['layout.configurations.status.editStatus.errorMessage.alreadyExists'],
        });
      },
    });
  }

  isInvalid(key: string) {
    const control = this.form.get(key);

    return {
      invalid: control?.invalid && (control?.dirty || control?.touched),
      message:
        control?.errors &&
        (control.errors['required'] 
          ? this.errorMessages.required[key] 
          : control.errors['minlength']
          ? this.errorMessages.minLength[key]
          : this.errorMessages.pattern[key]),
    };
  }

  getErrorMessage(field: string) {
    return this.formErrors?.find((obj: { field: string }) => obj.field === field)?.errorMessage || '';
  }
}
